import {
    createSlice,
    createEntityAdapter,
} from '@reduxjs/toolkit';
import { inplayEventData } from '~SpectateStore/thunks/inplayEventData';
import { eventUpdate } from '~SpectateStore/actions/eventUpdate';
import { marketSwitcherUpdate } from '~SpectateStore/actions/marketSwitcherUpdate';
import { eventInplayStart } from '~SpectateStore/actions/eventInplayStart';
import { eventWaitingToInplay } from '~SpectateStore/actions/eventWaitingToInplay';
import { storeCleanup } from '~SpectateStore/actions/storeCleanupActions';
import {
    dataPointersUpdate,
} from '~SpectateStore/actions/dataPointersActions';

export const SLICE_NAME = 'selections';

const setByEventIdMapping = (state) => {
    return state.ids.reduce((acc, selectionId) => {
        const selection = state.entities[selectionId];
        if (!acc[selection.event_id]) {
            acc[selection.event_id] = [];
        }
        acc[selection.event_id].push(selection.id);

        return acc;
    }, {});
};

export const selectionsAdapter = createEntityAdapter();

const selectionsSlice = createSlice({
    name: SLICE_NAME,
    initialState: selectionsAdapter.getInitialState({
        byEventId: {},
    }),
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                storeCleanup,
                (state, { payload : { eventIds } }) => {
                    const selectionIds = eventIds.reduce((acc, eventId) => {
                        const selectionIdsForEvent = state.byEventId[eventId] || [];
                        acc.push(...selectionIdsForEvent);

                        return acc;
                    }, []);

                    selectionsAdapter.removeMany(state, selectionIds);

                    state.byEventId = setByEventIdMapping(state);

                    return state;
                },
            )
            .addCase(
                inplayEventData.fulfilled,
                (state, { payload : { selections } }) => {
                    if (Object.keys(selections).length > 0) {
                        selectionsAdapter.addMany(state, selections);
                    }

                    state.byEventId = setByEventIdMapping(state);

                    return state;
                },
            )
            .addCase(
                eventUpdate,
                (state, { payload : { selections, selectionsToBeRemoved } }) => {
                    if(selections.length >= 1 || selectionsToBeRemoved.length >= 1) {
                        selectionsAdapter.removeMany(state, selectionsToBeRemoved);
                        selectionsAdapter.upsertMany(state, selections);
                    }

                    state.byEventId = setByEventIdMapping(state);

                    return state;
                },
            )
            .addCase(
                dataPointersUpdate,
                (state, { payload : { selections = {}, selectionsToBeRemoved = [] } }) => {
                    if (selectionsToBeRemoved.length) {
                        selectionsAdapter.removeMany(state, selectionsToBeRemoved);
                    }

                    if (Object.keys(selections).length > 0) {
                        selectionsAdapter.addMany(state, selections);
                    }

                    state.byEventId = setByEventIdMapping(state);

                    return state;
                },
            )
            .addCase(
                marketSwitcherUpdate,
                (state, { payload: { selections } }) => {
                    // Replace the entire collection with a new set of entities
                    selectionsAdapter.setAll(state, selections);
                    state.byEventId = setByEventIdMapping(state);
                }
            )
            .addCase(
                eventInplayStart,
                (state, { payload : { selections = {} } }) => {
                    selectionsAdapter.addMany(state, selections);
                    state.byEventId = setByEventIdMapping(state);
                },
            )
            .addCase(
                eventWaitingToInplay,
                (state, { payload : { eventId, changes } }) => {
                    Object.values(state.entities).forEach(selection => {
                        if (Number(selection.event_id) === Number(eventId)) {
                            selectionsAdapter.updateOne(state, {
                                id: selection.id,
                                changes,
                            });
                        }
                    });
                },
            );
    },
});

export default selectionsSlice.reducer;
